import React from 'react'
import { Clock, Target, FileText, Calendar } from 'lucide-react'
import { WeeklyStats, timesheetUtils } from '@/services/timesheetService'

interface WeeklyOverviewProps {
  stats: WeeklyStats | null
  loading?: boolean
}

const WeeklyOverview: React.FC<WeeklyOverviewProps> = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200 animate-pulse">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-3 md:h-4 bg-secondary-200 rounded w-16 md:w-20 mb-2"></div>
                <div className="h-5 md:h-6 bg-secondary-200 rounded w-12 md:w-16 mb-1"></div>
                <div className="h-2 md:h-3 bg-secondary-200 rounded w-10 md:w-12"></div>
              </div>
              <div className="w-6 h-6 md:w-8 md:h-8 bg-secondary-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const qualityLevel = timesheetUtils.getQualityLevel(stats.averageQualityScore)

  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
      {/* 本週總工時 */}
      <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs md:text-sm font-medium text-secondary-600">本週總工時</p>
            <p className="text-xl md:text-2xl font-bold text-secondary-800">
              {stats.weeklyTotalHours.toFixed(2)}h
            </p>
            <p className="text-xs md:text-sm text-secondary-500 mt-1">
              本月累計: {stats.monthlyTotalHours.toFixed(2)}h
            </p>
          </div>
          <Clock className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
        </div>
      </div>

      {/* 平均品質分數 */}
      <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs md:text-sm font-medium text-secondary-600">平均品質分數</p>
            <p className={`text-xl md:text-2xl font-bold ${qualityLevel.color}`}>
              {stats.averageQualityScore || '-'}
              {stats.averageQualityScore && '分'}
            </p>
            <p className="text-xs md:text-sm text-secondary-500 mt-1">
              {qualityLevel.text}
            </p>
          </div>
          <Target className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
        </div>
      </div>

      {/* 尚未提交份數 */}
      <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs md:text-sm font-medium text-secondary-600">尚未提交份數</p>
            <p className="text-xl md:text-2xl font-bold text-secondary-800">
              {stats.pendingSubmissions}份
            </p>
            <p className="text-xs md:text-sm text-secondary-500 mt-1">
              {stats.pendingSubmissions === 0 ? '全部已提交' : '待提交申報'}
            </p>
          </div>
          <FileText className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
        </div>
      </div>

      {/* 本月總工時 */}
      <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs md:text-sm font-medium text-secondary-600">本月總工時</p>
            <p className="text-xl md:text-2xl font-bold text-secondary-800">
              {stats.monthlyTotalHours.toFixed(2)}h
            </p>
            <p className="text-xs md:text-sm text-secondary-500 mt-1">
              涉及專案: {stats.projectsInvolved.length}個
            </p>
          </div>
          <Calendar className="w-6 h-6 md:w-8 md:h-8 text-purple-500" />
        </div>
      </div>
    </div>
  )
}

export default WeeklyOverview
